import { FSA } from 'flux-standard-action';
import { 
  assign as _assign,
  forEach as _forEach,
  isEqual as _isEqual,
  isObject as _isObject,
  sortBy as _sortBy,
} from 'lodash';
import { WarningsRequestApprovalPayload, WarningActionTypes, WarningsFsaTypes, WarningChangedPayload } from './warnings.action';
import { 
  SelectedTroughModel, 
  WashBasinModel, 
  ApronBackSplashModel, 
  BuiltInPartsModel, 
  PlateSurfaceModel, 
  AssemblyAccessoriesModel,
  DrainAccessoriesModel,
  configurationDefaults,
  ConfigurationDefaults,
  ConfigurationDefaultModels,
  TroughModel,
  GetDistanceModel,
  AssemblyAccessoriesDefaultModel,
  PlateBuilder,
  Configuration
} from 'franke-trough-dependencies';

export interface WarningsViewsState {
  selectedTrough?: {viewed: boolean};
  washBasinDimension?: {viewed: boolean};
  apronBackSplash?: {viewed: boolean};
  builtInParts?: {viewed: boolean};
  surface?: {viewed: boolean};
  assemblyAccessories?: {viewed: boolean};
  drainAccessories?: {viewed: boolean};
}

type SelectedTroughTypes = keyof SelectedTroughModel;

export interface WarningsChangedState {
  selectedTrough?: {[P in keyof SelectedTroughTypes]: boolean};
  washBasinDimension?: {[P in keyof WashBasinModel]: boolean};
  apronBackSplash?: {[P in keyof ApronBackSplashModel]: boolean};
  builtInParts?: {[P in keyof BuiltInPartsModel]: boolean};
  surface?: {[P in keyof PlateSurfaceModel]: boolean};
  assemblyAccessories?: {[P in keyof AssemblyAccessoriesModel]: boolean};
  drainAccessories?: {[P in keyof DrainAccessoriesModel]: boolean};
}

export interface WarningsResetItem {
  readonly affected: string[];
  required: boolean;
  values?: {[key: string]: boolean};
}

export interface WarningsResetState {
  trough: WarningsResetItem;
  qty: WarningsResetItem;
  typeOfInstallation: WarningsResetItem;
  plateWidth: WarningsResetItem;
  plateDepth: WarningsResetItem;
  distances: WarningsResetItem;
  cornerDesign: WarningsResetItem;
  apron: WarningsResetItem;
}

export interface WarningsState {
  approved?: boolean;
  changed?: WarningsChangedState;
  requireReset: WarningsResetState; 
  updatedAt: number;
  warning?: WarningsRequestApprovalPayload;
}

export type RequireResetKeys = 'trough' | 'qty' | 'typeOfInstallation' | 'plateWidth' | 'plateDepth' | 'distances' | 'cornerDesign' | 'apron';

export const warningsInitialState: WarningsState = {
  approved: undefined,
  changed: {},
  requireReset: {
    trough: {
      affected: ['qty', 'apron', 'towelRail', 'plateWidth', 'distances', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser', 'console', 'drain', 'siphon'], 
      required: false,
      values: undefined,
    },
    qty: {
      affected: ['apron', 'plateWidth', 'distances', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser'], 
      required: false,
      values: undefined,
    },
    typeOfInstallation: {
      affected: ['apron', 'distances', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser', 'cornerDesign', 'surgeEdgeOrBackSplash'], 
      required: false,
      values: undefined,
    },
    plateWidth: {
      affected: ['apron', 'distances', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser'], 
      required: false,
      values: undefined,
    },
    plateDepth: {
      affected: ['tapHole', 'soapDispenserHole'], 
      required: false,
      values: undefined,
    },
    distances: {
      affected: ['apron', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser'], 
      required: false,
      values: undefined,
    },
    cornerDesign: {
      affected: ['distances', 'tapHole', 'soapDispenserHole', 'cutOutDisposals', 'paperTowelDispensers', 'tissueDispenser'], 
      required: false,
      values: undefined,
    },
    apron: {
      affected: ['towelRail', 'paperTowelDispensers', 'tissueDispenser'], 
      required: false,
      values: undefined,
    },
  },
  updatedAt: Date.now(),
  warning: undefined,
};

export const warningsReducer = (
  state: WarningsState = warningsInitialState, 
  action: FSA<WarningActionTypes, WarningsFsaTypes>
): WarningsState => {
  switch (action.type) {
    case WarningActionTypes.Approve: 
      return _assign<{}, WarningsState, WarningsState>({}, state, {
        approved: true,
        requireReset: state.requireReset,
        updatedAt: Date.now(),
        warning: undefined,
      });
    case WarningActionTypes.Changed:
      const changed = action.payload && discoverChanges(action.payload as WarningChangedPayload);
      return _assign({}, state, {
        changed,
        requireReset: changed && discoverIfResetIsRequired(state.requireReset, changed) || state.requireReset,
        updatedAt: Date.now(),
      });
    case WarningActionTypes.Clear: 
      return _assign<WarningsState, {updatedAt: number}>(JSON.parse(JSON.stringify(warningsInitialState)), {
        updatedAt: Date.now(),
      });
    case WarningActionTypes.Decline:
      return _assign<{}, WarningsState, WarningsState>({}, state, {
        approved: false,
        requireReset: state.requireReset,
        updatedAt: Date.now(),
        warning: undefined,
      });
    case WarningActionTypes.RequestApproval: 
      return _assign<{}, WarningsState, WarningsState>({}, state, {
        approved: false,
        requireReset: state.requireReset,
        updatedAt: Date.now(),
        warning: action.payload as WarningsRequestApprovalPayload,
      });
    default:
      return state;
  }
};

const discoverIfResetIsRequired = (items: WarningsResetState, changed: WarningsChangedState): WarningsResetState => {
  Object.keys(items).forEach(itemKey => {
    const item: WarningsResetItem = items[itemKey];
    const values: boolean[] = [];
    item.values = {};
    Object.keys(changed).forEach(
      keys => Object.keys(changed[keys]).forEach(key => {
        if (item.affected.indexOf(key) > -1) {
          values.push(changed[keys][key]);
          if (item.values) {
            item.values[key] = changed[keys][key];
          }
        }
      })
    );
    items[itemKey].required = values.indexOf(true) > -1 ? true : false;    
  });
  return items;
};

const discoverIfDefaultChanged = (defaults: ConfigurationDefaults<unknown, unknown>, confDefaults:  ConfigurationDefaults<unknown, unknown>): boolean | undefined => {
  if (_isObject(defaults) && _isObject(confDefaults)) {
    return defaults.default !== confDefaults.default;
  }
  return defaults !== confDefaults;
};

const discoverChanges = (payload: WarningChangedPayload): WarningsChangedState => {
  const {
    configuration,
    defaults,
    initialRequest
  } = payload;

  const trough = configuration.selectedTrough && configuration.selectedTrough.trough as TroughModel;
  let plateBuilder: PlateBuilder | undefined;

  if (trough) {
    const conf = new Configuration(configuration);
    plateBuilder = new PlateBuilder(conf, trough);
  }
  
  const changed: WarningsChangedState = {};

  _forEach(defaults, (prevDefaults: ConfigurationDefaultModels, key) => {
    const origDefaults = configurationDefaults[key];
    
    if (configuration[key]) {
      changed[key] = {};
      _forEach(prevDefaults, (element, subKey) => {
        const isNewTrough = !initialRequest && configuration.troughId !== defaults.selectedTrough.troughId ? true : false;
        if (!configuration[key][subKey] || configuration[key][subKey] === null) {
          switch (subKey) {
            case 'troughId':              
              changed[key][subKey] = configuration.troughId !== element;
              break;
            default: 
              changed[key][subKey] = false;
          }
        } else {
          const hasChanged = !initialRequest ? discoverIfDefaultChanged(element, origDefaults[subKey]) : true;
          switch (subKey) {
            case 'qty':
              changed[key][subKey] = configuration.selectedTrough && configuration.selectedTrough.qty > 3;
              break;

            case 'troughId':
              changed[key][subKey] = configuration.troughId !== element;
              break;

            case 'apron':
              if (configuration.apronBackSplash && configuration.apronBackSplash.apron) {
                // FIXME: We override apron default without a hardcoded value... this is because FTD updates the apron default so that we know what is the prevDefault so that we can update distances...
                // So bloody complicated! I just do not have the patience to spend so much time refactoring to get prevConfig in FTD (ideal situation)
                const apronDefault = 'without'; // (element as any).default || configuration.defaults.apronBackSplash.apron.default || 'without';
                changed[key][subKey] = apronDefault !== configuration[key][subKey] || configuration[key]['restorablePlateDimensions'] ? true : false;
              } else {
                changed[key][subKey] = false;
              }
              break;

            case 'distances':
              const distancesDefault = plateBuilder && plateBuilder.calc.troughInitialPositions();
              const distances = configuration[key][subKey] as number[];
              
              if ((distancesDefault && distancesDefault.length) === distances.length) {
                changed[key][subKey] = !_isEqual(_sortBy(distancesDefault), _sortBy(distances));
              } else {
                changed[key][subKey] = !_isEqual(_sortBy(configuration.defaults.washBasinDimension.distances.default), _sortBy(distances));
              }
              break;

            case 'console':
              if (configuration.assemblyAccessories && configuration.assemblyAccessories.selection) {
                const consoleDefault = (element as AssemblyAccessoriesDefaultModel['console']).default || configuration.defaults.assemblyAccessories.console.default || 'stainlessSteel';
                changed[key][subKey] = consoleDefault !== configuration[key][subKey] ? true : false;
              } else {
                changed[key][subKey] = false;
              }
              break;

            case 'plateWidth':
              const defaultPlateWidth = plateBuilder && plateBuilder.calc.plateMinWidth().total || configuration.defaults[key][subKey].default;            
              changed[key][subKey] = !isNewTrough && defaultPlateWidth !== configuration[key][subKey] ? true : false;
              break;
  
            case 'plateDepth':
              changed[key][subKey] = configuration.defaults[key][subKey].default !== configuration[key][subKey] ? true : false;
              break;

            case 'cornerDesign': 
              changed[key][subKey] = configuration[key][subKey] !== configurationDefaults.washBasinDimension.cornerDesign.default;
              break;

            default: 
              if (_isObject(element)) {
                if (!hasChanged) {
                  changed[key][subKey] = (element as any).default !== configuration[key][subKey] ? true : false;
                } else {
                  changed[key][subKey] = hasChanged && ((element as any).default !== configuration[key][subKey]) ? true : false;
                }
              } else {
                if (!hasChanged) {
                  changed[key][subKey] = element !== configuration[key][subKey] ? true : false;
                } else {
                  changed[key][subKey] = hasChanged && element !== configuration[key][subKey] ? true : false;
                }
              }
          }
        }
      });
    }
  });
  
  return changed;
};